import { useEffect, useState } from "react"
import type { Chat } from "~models"
import { limit, queryMessagesByChatIdentity } from "~services/sqlite"

const MAX_SQLITE_ROUNDS = 100
const MAX_SERVER_ROUNDS = 30

/**
 * 搜索页进入时补全会话消息：内存 relink → 本地 SQLite 分页 → 游戏群再拉服务端历史。
 * 聊天室列表只渲染已加载窗口，搜索页需单独灌入完整可搜范围。
 */
export function useChatSearchMessages(
  chat: Chat | null | undefined,
  websocketReady: boolean,
): { hydrationTick: number; isHydrating: boolean } {
  const [hydrationTick, setHydrationTick] = useState(0)
  const [isHydrating, setIsHydrating] = useState(false)

  useEffect(() => {
    if (!chat) {
      setIsHydrating(false)
      return undefined
    }

    let cancelled = false
    setIsHydrating(true)

    const hydrate = async () => {
      try {
        chat.relinkMessagesFromStore()

        if (!chat.isGameGroup) {
          let offset = 0
          let rounds = 0
          while (!cancelled && rounds < MAX_SQLITE_ROUNDS) {
            const batch = await queryMessagesByChatIdentity(chat, offset)
            if (!batch.length) break
            chat.prependMessages(batch)
            offset += batch.length
            rounds += 1
            if (batch.length < limit) break
          }
        } else if (websocketReady) {
          if (chat.sortedMessages.length === 0) {
            chat.setIsNoMoreData(false)
            await chat.publicReadChatHistory(null, { manageSearching: false })
          }

          let rounds = 0
          while (!cancelled && rounds < MAX_SERVER_ROUNDS && !chat.isNoMoreData) {
            const before = chat.sortedMessages.length
            const oldest = chat.sortedMessages[chat.sortedMessages.length - 1]
            await chat.publicReadChatHistory(oldest?.id ?? null, { manageSearching: false })
            if (chat.sortedMessages.length === before) break
            rounds += 1
          }
        }

        chat.relinkMessagesFromStore()
      } finally {
        if (!cancelled) {
          setHydrationTick((t) => t + 1)
          setIsHydrating(false)
        }
      }
    }

    void hydrate()

    return () => {
      cancelled = true
    }
  }, [chat?.chat_id, websocketReady])

  return { hydrationTick, isHydrating }
}
